// src/components/CargarSolicitud.js
import React, { useState, useEffect } from 'react';
import { MdOutlineCancel } from 'react-icons/md';
import { useStateContext } from '../../contexts/ContextProvider';
import { useSolicitudesMutations } from '../../hooks'

import { FooterPages } from '..';
import { getCentroCosto } from '../../services/CentroCostos';

import { fetchUser } from '../../utils/fetchUser';
import { Toaster, toast } from 'react-hot-toast';

import * as XLSX from 'xlsx';

const CargarSolicitud = ({ onChangeVecino }) => {
  const { currentColor, isClicked, setIsClicked } = useStateContext();

  const [centroData, setCentroData] = useState([]);
  const [cCosto, setCcosto] = useState('');
  const [fileName, setFileName] = useState('');
  const [excelRows, setExcelRows] = useState([]); // filas parseadas
  const [lineCount, setLineCount] = useState(0);
  const [totalMachines, setTotalMachines] = useState(0);
  const [loadingParse, setLoadingParse] = useState(false);
  const [sending, setSending] = useState(false);
  const [createdRequestNumber, setCreatedRequestNumber] = useState(null);
  const {addSolicitudExcel } = useSolicitudesMutations();

  const inputClass = "text-[11px] border border-gray-300 p-1.5 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-400";

  const userInfo = fetchUser();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const misCentros = await getCentroCosto();
        setCentroData(misCentros || []);
      } catch (err) {
        console.error('Error cargando centros de costo', err);
      }
    };
    fetchData();
  }, []);

  const handleChangeCentro = (e) => {
    setCcosto(e.target.value);
  };

  // Helpers
  const normalize = (s = '') => String(s).trim().toLowerCase();

  const validateHeaders = (headers) => {
    // columnas requeridas (case-insensitive)
    const required = ['cantidad', 'maquina', 'capacidad', 'fecha en obra', 'permanencia','comentario'];
    const found = headers.map(h => normalize(h));
    const missing = required.filter(r => !found.includes(r));
    return { ok: missing.length === 0, missing };
  };

  // Parse file when selected
  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFileName(file.name);
    setLoadingParse(true);
    setExcelRows([]);
    setLineCount(0);
    setTotalMachines(0);
    setCreatedRequestNumber(null);

    try {
      const arrayBuffer = await file.arrayBuffer();
      const workbook = XLSX.read(arrayBuffer, { type: 'array' });

      const sheetName = workbook.SheetNames.find(name => normalize(name) === 'solicitud');
      if (!sheetName) {
        toast.error('No se encontró la hoja "Solicitud" en el archivo.');
        setLoadingParse(false);
        return;
      }

      const sheet = workbook.Sheets[sheetName];
      // Convertir a JSON tomando la primera fila como cabeceras
      const rawJson = XLSX.utils.sheet_to_json(sheet, { defval: '' }); // defval evita undefined
      if (!rawJson || rawJson.length === 0) {
        toast.error('La hoja "Solicitud" está vacía o no tiene filas de datos.');
        setLoadingParse(false);
        return;
      }

      // Validar encabezados usando las keys del primer objeto
      const headers = Object.keys(rawJson[0]);
      const { ok, missing } = validateHeaders(headers);
      if (!ok) {
        toast.error(`Faltan columnas requeridas: ${missing.join(', ')}`);
        setLoadingParse(false);
        return;
      }

      // Normalizar y mapear filas al formato deseado
      const mapped = rawJson.map((r, idx) => {
        // buscar valores por key flexible (case-insensitive)
        const getField = (fieldName) => {
          const key = Object.keys(r).find(k => normalize(k) === fieldName);
          return key ? r[key] : '';
        };

        const cantidadRaw = getField('cantidad');
        const cantidad = Number(String(cantidadRaw).replace(',', '.')) || 0;

        const maquina = String(getField('maquina') || '').trim();
        const capacidad = String(getField('capacidad') || '').trim();

        // Fecha: intentar convertir con XLSX (puede venir como Date) o parsear string
        let fechaEnObraRaw = getField('fecha en obra');
        let fechaISO = null;
        if (fechaEnObraRaw instanceof Date && !isNaN(fechaEnObraRaw)) {
          fechaISO = fechaEnObraRaw.toISOString();
        } else if (typeof fechaEnObraRaw === 'number') {
          // Excel stores dates as serial numbers sometimes
          const date = XLSX.SSF.parse_date_code(fechaEnObraRaw);
          if (date) {
            fechaISO = new Date(date.y, date.m - 1, date.d).toISOString();
          }
        } else if (fechaEnObraRaw && String(fechaEnObraRaw).trim() !== '') {
          const parsed = new Date(String(fechaEnObraRaw));
          if (!isNaN(parsed)) fechaISO = parsed.toISOString();
          else fechaISO = String(fechaEnObraRaw).trim(); // mantener string por si viene dd/mm/yyyy
        }

        const permanencia = String(getField('permanencia') || '').trim();

        const comentario = String(getField('comentario') || '').trim();

        return {
          __rowIndex: idx + 1, // para referencia (fila en excel: 1 cabecera)
          cantidad,
          maquina,
          capacidad,
          fechaEnObra: fechaISO,
          permanencia,
          comentario,
          raw: r
        };
      });

      // Filtrar filas que contengan al menos algo válido (por ejemplo, máquina o cantidad)
      const filtered = mapped.filter(row => (row.maquina && row.cantidad > 0) || row.capacidad || row.fechaEnObra || row.permanencia);

      // Calcular sumas
      const lineasLeidas = filtered.length;
      const machinesSum = filtered.reduce((acc, r) => acc + (isNaN(r.cantidad) ? 0 : r.cantidad), 0);

      setExcelRows(filtered);
      setLineCount(lineasLeidas);
      setTotalMachines(machinesSum);

      toast.success(`Archivo leido: ${lineasLeidas} línea(s) válidas, ${machinesSum} máquinas totales.`);

    } catch (err) {
      console.error('Error parseando Excel:', err);
      toast.error('Error al leer el archivo. Revisa que sea un .xlsx válido.');
    } finally {
      setLoadingParse(false);
    }
  };

  // Construir JSON y enviar a API
  const handleEnviarSolicitud = async (e) => {
    e.preventDefault();
    if (!cCosto) {
      toast.error('Seleccione un Centro de Costo.');
      return;
    }
    if (!fileName) {
      toast.error('Adjunte un archivo Excel con la hoja "Solicitud".');
      return;
    }
    if (!excelRows || excelRows.length === 0) {
      toast.error('El archivo no contiene filas válidas para enviar.');
      return;
    }

    const payload = {
      empresa: userInfo.empresa,
      fechaCreacion: new Date().toISOString(),
      centroCostoId: cCosto,
      creadoPor: userInfo.usuario,
      totalMaquinasSolicitadas: totalMachines,
      lineasLeidas: lineCount,
      filas: excelRows.map(r => ({
        cantidad: r.cantidad,
        maquina: r.maquina,
        capacidad: r.capacidad,
        fechaEnObra: r.fechaEnObra,
        permanencia: r.permanencia,
        comentario: r.comentario
      }))
    };

    try {
      setSending(true);
      // Reemplaza la URL por la de tu API
  
      const res = await addSolicitudExcel.mutateAsync( JSON.stringify(payload) );

      if (!res) {
        const errorBody = await res.text();
        throw new Error(`Error en API: ${res.status} ${errorBody}`);
      }

      // const data = await res.json();
      // Se espera que la API devuelva { numeroSolicitud: '...' } o similar
      const numero = res.nuevaSolicitud || res.nro || res.id || null;
      setCreatedRequestNumber(numero);
      toast.success(`Solicitud creada${numero ? ` N° ${numero}` : ''}`);

      // opcional: refrescar lista padre
      if (typeof onChangeVecino === 'function') onChangeVecino('REFRESH');

      // cerrar modal
      setIsClicked(!isClicked.cargarsolicitud);

    } catch (err) {
      console.error('Error enviando solicitud:', err);
      toast.error('Error al enviar la solicitud. Revisa la consola o la API.');
    } finally {
      setSending(false);
    }
  };

  const handleClose = () => {
    setIsClicked(!isClicked.cargarsolicitud);
  };

  return (
    <form onSubmit={handleEnviarSolicitud}>
      <div className="bg-half-transparent w-full fixed nav-item top-0 right-0 z-50">
        <div className="overflow-y-auto float-right h-screen duration-500 ease-in-out dark:text-gray-200 transition-all dark:bg-[#484B52] bg-gray-100  w-full md:w-3/4 p-6">
          <div className="flex justify-between items-center px-4 py-2 border-b border-gray-200 bg-white rounded-t-md shadow-sm">
            <div className="flex items-center gap-2">
              <div className="w-2.5 h-2.5 rounded-full bg-blue-500 animate-pulse" />
              <p className="text-gray-800 font-semibold text-base md:text-lg">Cargar Solicitud</p>
            </div>

            <button
              type="button"
              onClick={handleClose}
              className="text-gray-400 hover:text-red-500 transition-colors duration-150 p-2 rounded-full hover:bg-gray-100"
              aria-label="Cerrar"
            >
              <MdOutlineCancel className="text-xl md:text-2xl" />
            </button>
          </div>

          <div className="bg-gray-200 p-6 rounded-lg shadow-lg mt-4">
            <div className="grid grid-cols-3 md:grid-cols-6 gap-4">
              <div className="col-span-3 md:col-span-2">
                <label className="text-xs font-semibold mb-1 block">Centro Costo*</label>
                <select className={inputClass + " w-full"} name="cCosto" value={cCosto} onChange={handleChangeCentro}>
                  <option value="">-- Seleccione --</option>
                  {centroData?.map((item) => (
                    <option key={item._id} value={item._id}>{item.codigo} - {item.nombreProyecto || ''}</option>
                  ))}
                </select>
              </div>

              <div className="col-span-3 md:col-span-4">
                <label className="text-xs font-semibold mb-1 block">Archivo (xlsx) - Hoja "Solicitud"</label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept=".xlsm"
                    onChange={handleFileChange}
                    className="text-sm"
                  />
                  <div className="text-xs text-gray-600">
                    {loadingParse ? 'Leyendo archivo...' : (fileName ? fileName : 'No hay archivo seleccionado')}
                  </div>
                </div>
                <p className="mt-2 text-xs text-gray-500">El archivo debe contener la hoja llamada <strong>Solicitud</strong> y columnas: Cantidad, Maquina, Capacidad, Fecha en Obra, Permanencia. Primera fila = encabezado.</p>
              </div>
            </div>
          </div>

          <div className="mt-5 space-y-4">
            <div className="bg-white p-6 rounded-lg shadow-lg">
              <div className="flex justify-between items-center">
                <div>
                  <h4 className="text-sm font-semibold">Resumen de archivo</h4>
                  <p className="text-xs text-gray-600 mt-1">
                    Líneas leídas: <span className="font-medium">{lineCount}</span> — Máquinas totales: <span className="font-medium">{totalMachines}</span>
                  </p>
                  {createdRequestNumber && (
                    <p className="text-xs text-green-700 mt-1">Solicitud creada N°: <span className="font-bold">{createdRequestNumber}</span></p>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="submit"
                    disabled={sending || loadingParse || !excelRows.length}
                    className={`px-4 py-2 rounded-md text-white text-sm ${sending ? 'opacity-60' : ''}`}
                    style={{ backgroundColor: currentColor || '#2563eb' }}
                  >
                    {sending ? 'Enviando...' : 'Crear Solicitud'}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      // limpiar
                      setFileName('');
                      setExcelRows([]);
                      setLineCount(0);
                      setTotalMachines(0);
                      setCreatedRequestNumber(null);
                    }}
                    className="px-3 py-2 border border-gray-300 rounded-md text-sm bg-white hover:bg-gray-50"
                  >
                    Limpiar
                  </button>
                </div>
              </div>

              {/* Preview tabla (primeras 5 filas) */}
              <div className="mt-4">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs">
                    <thead>
                      <tr className="text-left">
                        <th className="p-2 font-medium text-gray-600">#</th>
                        <th className="p-2 font-medium text-gray-600">Cantidad</th>
                        <th className="p-2 font-medium text-gray-600">Máquina</th>
                        <th className="p-2 font-medium text-gray-600">Capacidad</th>
                        <th className="p-2 font-medium text-gray-600">Fecha en Obra</th>
                        <th className="p-2 font-medium text-gray-600">Permanencia</th>
                        <th className="p-2 font-medium text-gray-600">Comentario</th>
                      </tr>
                    </thead>
                    <tbody>
                      {excelRows.slice(0, 10).map((r, i) => (
                        <tr key={i} className="border-t">
                          <td className="p-2">{r.__rowIndex}</td>
                          <td className="p-2">{r.cantidad}</td>
                          <td className="p-2">{r.maquina}</td>
                          <td className="p-2">{r.capacidad}</td>
                          <td className="p-2">{r.fechaEnObra ? (new Date(r.fechaEnObra).toLocaleDateString()) : ''}</td>
                          <td className="p-2">{r.permanencia}</td>
                          <td className="p-2">{r.comentario}</td>
                        </tr>
                      ))}
                      {excelRows.length === 0 && (
                        <tr>
                          <td colSpan="6" className="p-2 text-gray-500">No hay filas cargadas</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
                {excelRows.length > 10 && (
                  <p className="text-xs text-gray-500 mt-2">Mostrando primeras 10 filas de {excelRows.length}</p>
                )}
              </div>
            </div>

            <Toaster position="top-right" toastOptions={{ style: { zIndex: 9999 } }} />
            <FooterPages />
          </div>
        </div>
      </div>
    </form>
  );
};

export default CargarSolicitud;
